/**
 * tutorReleaseAdapter — « Libérer des créneaux » : ce que le brouillon envoie, et ce que l'aperçu du
 * serveur dit au masteur, carte par carte.
 *
 * Portage littéral de `tutor/availability/releaseSlotsModel.js` (le corps de l'aperçu et de
 * l'application, les lignes de chaque carte) et du rendu de `ReleaseSlotsSteps.js`
 * (`ReleaseSlotsStudentsStep` : l'ordre des cartes, les rattrapages, les sièges partis, les stages,
 * les essais, les lives non concernés). Les règles vivent au serveur : on ne fait que traduire ses
 * verdicts dans les mots du tuteur.
 */
import moment from "moment"
import "moment/locale/fr"

import {
    type UITutorReleaseCard,
    type UITutorReleasePreview,
    type UITutorReleaseTone,
} from "../../../ui/types/tutorDispoOffres"
import {
    type ApiTutorWeekSlot,
    chipLabelOf,
    dayLabelOf,
    frTime,
    slotDayOf,
    slotKeyOf,
    slotRangeLabel,
    slotStartOf,
} from "../shared/tutorWeekAdapter"

// ── Formes lâches de l'aperçu ───────────────────────────────────────────────

type ApiReleaseStay = {
    freed?: string | null
    new_slot?: string | null
    new_group?: boolean | null
    effective_on?: string | null
}

export type ApiReleaseStudent = {
    student_id?: number | string | null
    student_name?: string | null
    student_firstname?: string | null
    outcome?: string | null
    freed?: string[] | null
    stays?: ApiReleaseStay[] | null
    new_slot?: string | null
    effective_on?: string | null
    choice_deadline?: string | null
    enters_cohort?: string | null
    paused_until?: string | null
    leaving?: string[] | null
    split_leaving?: string[] | null
    suggested_slots?: string[] | null
    covered_dates?: string[] | null
}

type ApiReleaseRattrapage = { student_id?: number | string | null; student_name?: string | null; date?: string | null }

export type ApiReleaseStage = {
    live_session_id?: number | string | null
    outcome?: string | null
    day_time?: string | null
    dates?: string[] | null
    student_names?: string[] | null
    keeps_rest?: boolean | null
}

type ApiReleaseTrial = { live_session_id?: number | string | null; date?: string | null; student_name?: string | null }

type ApiReleaseKeptSeat = {
    student_id?: number | string | null
    student_name?: string | null
    day_time?: string | null
    subjects?: string | null
}

type ApiReleaseUntouchedLive = {
    live_session_id?: number | string | null
    day_time?: string | null
    room_type?: string | null
}

/** La réponse de `POST tutor/availability/release/preview`. */
export type ApiReleasePreview = {
    refusal?: string | null
    students?: ApiReleaseStudent[] | null
    rattrapages?: ApiReleaseRattrapage[] | null
    stages?: ApiReleaseStage[] | null
    trials?: ApiReleaseTrial[] | null
    kept_seats?: ApiReleaseKeptSeat[] | null
    untouched_lives?: ApiReleaseUntouchedLive[] | null
}

// ── Le corps envoyé ─────────────────────────────────────────────────────────

export const RELEASE_MOTIF = { SCHEDULE_CHANGE: "schedule_change", UNAVAILABLE: "unavailable" } as const
export type ReleaseMotif = (typeof RELEASE_MOTIF)[keyof typeof RELEASE_MOTIF]

const STUDENT_OUTCOME = { KEPT: "kept", NEW_GROUP: "new_group", REATTRIBUTION: "reattribution" }
const STAGE_OUTCOME = { COVERED: "stage_covered", REATTRIBUTED: "stage_reattributed" }

/** Ce que le brouillon change aux créneaux proposés, par clé comme le serveur (`weekDiff`). */
export function weekDiff(serverOpenSlots: string[], draftOpenSlots: string[]) {
    const serverKeys = new Set(serverOpenSlots.map(slotKeyOf))
    const draftKeys = new Set(draftOpenSlots.map(slotKeyOf))
    return {
        new_slots: draftOpenSlots.filter((s) => !serverKeys.has(slotKeyOf(s))).map(slotStartOf),
        dropped_slots: serverOpenSlots.filter((s) => !draftKeys.has(slotKeyOf(s))).map(slotStartOf),
    }
}

/**
 * Le corps de l'aperçu ET de l'application (`buildReleaseBody`). Tout arrêter libère chaque créneau
 * et ne laisse aucune semaine à confirmer : ni la sélection ni le brouillon ne partent.
 */
export function buildReleaseBody(input: {
    motif: ReleaseMotif
    selectedSlots: string[]
    serverOpenSlots: string[]
    draftOpenSlots: string[] | null
    oneOffLiveIds?: (number | string)[]
}) {
    const { motif, selectedSlots, serverOpenSlots, draftOpenSlots, oneOffLiveIds = [] } = input
    if (motif === RELEASE_MOTIF.UNAVAILABLE) {
        return { slots: [], all: true, motif, new_slots: [], dropped_slots: [] }
    }
    return {
        slots: selectedSlots.map(slotKeyOf),
        all: false,
        motif,
        ...weekDiff(serverOpenSlots, draftOpenSlots ?? serverOpenSlots),
        // L'essai ou le stage d'où part le geste : le serveur lit son heure sur le live.
        ...(oneOffLiveIds.length ? { live_session_ids: oneOffLiveIds } : {}),
    }
}

/** Le créneau engagé qui porte ce live, s'il le tient encore — lu sur le live, jamais sur l'heure. */
export function engagedSlotOfLive(
    engagedSlots: ApiTutorWeekSlot[],
    liveSessionId: number | string | null | undefined
): ApiTutorWeekSlot | null {
    if (liveSessionId == null) return null
    return (
        engagedSlots.find((slot) => (slot.live_session_ids ?? []).map(Number).includes(Number(liveSessionId))) ?? null
    )
}

/** Un créneau engagé comme le tuteur le connaît, par son live : « Mardi 18h30 », sinon « Mardi 18h-19h ». */
export function engagedSlotLabel(slot: ApiTutorWeekSlot): string {
    const flat = slot.slot ?? ""
    return `${dayLabelOf(slotDayOf(flat))} ${chipLabelOf({
        engaged: true,
        time: slotKeyOf(flat).split(" ")[1],
        label: slotRangeLabel(flat),
        liveTimes: slot.live_times,
    })}`
}

// ── Les libellés ────────────────────────────────────────────────────────────

/** « Tuesday 18:00 » → « mardi à 18h » — un créneau de la semaine, jamais converti de fuseau. */
export const dayTimeLabel = (slotString: string): string =>
    `${dayLabelOf(slotDayOf(slotString)).toLowerCase()} à ${frTime(slotKeyOf(slotString).split(" ")[1])}`

const parseLocal = (dateTime: string) => moment(dateTime, "YYYY-MM-DD HH:mm").locale("fr")

/** « 2026-10-21 18:00 » → « mercredi 21/10 ». */
const dayDateLabel = (dateTime: string): string => parseLocal(dateTime).format("dddd DD/MM")

/** « 2026-10-21 18:00 » → « mercredi 21/10 à 18h ». */
const dayDateTimeLabel = (dateTime: string): string => {
    const m = parseLocal(dateTime)
    return `${m.format("dddd DD/MM")} à ${frTime(m.format("HH:mm"))}`
}

/** « 2026-10-20 » → « 20/10 ». */
const shortDateLabel = (date: string) => moment(date, "YYYY-MM-DD").format("DD/MM")

/** Comment l'étape de la semaine nomme l'essai ou le stage d'où part le geste : « essai du mardi à 17h ». */
export const oneOffLabel = (roomType: string, dayTime: string): string =>
    `${roomType === "trial" ? "essai" : "stage"} du ${dayTimeLabel(dayTime)}`

const newSlotSince = (student: { new_slot?: string | null; effective_on?: string | null }) =>
    `${student.new_slot ? dayTimeLabel(student.new_slot) : ""}${
        student.effective_on ? ` dès le ${shortDateLabel(student.effective_on)}` : ""
    }`.trim()

/** « Son cours du mardi à 18h » / « Ses cours du mardi à 18h et du mardi à 19h ». */
const coursesLabel = (slots: string[] = []): string =>
    slots.length > 1
        ? `Ses cours du ${slots.map(dayTimeLabel).join(" et du ")}`
        : `Son cours du ${dayTimeLabel(slots[0] ?? "")}`

export type ReleaseStudentRow = {
    tag: string
    staysWithTutor: boolean
    atRisk: boolean
    lost: boolean
    alert: string | null
    lines: string[]
    suggestion: string | null
    suggestedSlots: string[]
}

/**
 * Un élève de l'aperçu → sa carte (`studentRow`). L'étiquette est le côté du tuteur : un élève
 * réattribué dont la famille peut encore choisir un de ses créneaux est « risque de perte », sinon
 * « Élève perdu » ; « nouveau tuteur » est le côté de la famille, jamais écrit ici.
 */
export function studentRow(student: ApiReleaseStudent): ReleaseStudentRow {
    const lines: string[] = []
    const staysWithTutor = student.outcome !== STUDENT_OUTCOME.REATTRIBUTION
    const atRisk = !staysWithTutor && !!student.choice_deadline
    const lost = !staysWithTutor && !atRisk
    const freed = student.freed ?? []
    const courses = freed.length ? coursesLabel(freed) : null
    const several = freed.length > 1

    if ((student.stays?.length ?? 0) > 1) {
        // Plusieurs cours libérés restent avec lui : une ligne par cours, chacun sur son créneau.
        for (const stay of student.stays ?? []) {
            const since = newSlotSince(stay)
            lines.push(
                stay.new_group
                    ? `${coursesLabel([stay.freed ?? ""])} passe dans un nouveau groupe, le ${since}`
                    : `${coursesLabel([stay.freed ?? ""])} passe au ${since}`
            )
        }
    } else if (student.outcome === STUDENT_OUTCOME.KEPT) {
        lines.push(courses ? `${courses} passe au ${newSlotSince(student)}` : newSlotSince(student))
    } else if (student.outcome === STUDENT_OUTCOME.NEW_GROUP) {
        lines.push(
            courses
                ? `${courses} passe dans un nouveau groupe, le ${newSlotSince(student)}`
                : `Nouveau groupe ${newSlotSince(student)}`
        )
    } else {
        const pause = student.paused_until ? "En pause. " : ""
        if (atRisk) {
            const passes = several ? "ils passent" : "il passe"
            lines.push(
                `${pause}${courses ?? "Ce cours"} : sans réponse avant le ${dayDateTimeLabel(student.choice_deadline as string)}, ${passes} à un autre tuteur.`
            )
        } else {
            if (courses) lines.push(`${courses} chez toi ${several ? "s'arrêtent" : "s'arrête"}.`)
            if (student.enters_cohort === "now") {
                lines.push(
                    `${pause}${several ? "Tu perds ces cours" : "Tu perds ce cours"} : tu n'as pas assez de créneaux libres à proposer à sa famille.`
                )
            } else if (pause) {
                lines.push(pause.trim())
            }
        }
        // Le bloc part avec le cours libéré, sauf si la famille reste avec le tuteur.
        for (const slot of student.leaving ?? []) {
            lines.push(
                atRisk
                    ? `Si sa famille change de tuteur, son cours du ${dayTimeLabel(slot)} part aussi.`
                    : `Son cours du ${dayTimeLabel(slot)} chez toi s'arrête aussi.`
            )
        }
        if (student.split_leaving?.length) {
            lines.push(
                `Sa famille peut aussi te garder pour ${coursesLabel(student.split_leaving).replace(/^S/, "s")} seulement.`
            )
        }
    }

    for (const date of student.covered_dates ?? []) {
        lines.push(`${dayDateLabel(date)} : un remplaçant est demandé.`)
    }

    const suggestedSlots = staysWithTutor ? [] : (student.suggested_slots ?? [])
    const who = student.student_firstname || student.student_name || "Cet élève"

    return {
        tag: staysWithTutor ? "reste avec toi" : atRisk ? "risque de perte" : "Élève perdu",
        staysWithTutor,
        atRisk,
        lost,
        alert: atRisk ? "Nous proposons tes créneaux à sa famille : elle peut les refuser et changer de tuteur." : null,
        lines: lines.filter(Boolean),
        suggestion: suggestedSlots.length
            ? `${who} est aussi disponible le ${suggestedSlots.map(dayTimeLabel).join(", le ")} : ${suggestedSlots.length > 1 ? "ouvre ces créneaux" : "ouvre ce créneau"} pour le garder.`
            : null,
        suggestedSlots,
    }
}

const severityOf = (row: ReleaseStudentRow) => (row.lost ? 0 : row.atRisk ? 1 : 2)

/** Les élèves de l'aperçu avec leur carte, la plus grave en tête : perdu, à risque, puis ceux qui restent. */
export function studentRowsBySeverity(students: ApiReleaseStudent[] = []) {
    return students
        .map((student) => ({ student, row: studentRow(student) }))
        .sort((a, b) => severityOf(a.row) - severityOf(b.row))
}

export const rattrapageLine = (rattrapage: { date?: string | null }): string =>
    `${dayDateTimeLabel(rattrapage.date ?? "")}. Son crédit est gardé.`

/** Ce qui arrive à un stage, sur la carte de chacun de ses élèves (`stageLine`). */
export function stageLine(stage: ApiReleaseStage): string {
    const dates = stage.dates ?? []
    if (stage.outcome === STAGE_OUTCOME.REATTRIBUTED) {
        const span =
            dates.length > 1
                ? `La suite de son stage, du ${dayDateLabel(dates[0])} au ${dayDateLabel(dates[dates.length - 1])},`
                : `Son stage du ${dayDateLabel(dates[0] ?? "")}`
        return `${span} passe à un autre tuteur, pour garder le même tuteur jusqu'au bout.`
    }
    const sessions = dates.length > 1 ? "pour les séances du" : "pour la séance du"
    return `Son stage du ${dayTimeLabel(stage.day_time ?? "")} continue sans toi : un remplaçant est demandé ${sessions} ${dates.map(dayDateLabel).join(", le ")}.${stage.keeps_rest ? " Tu gardes le reste du stage." : ""}`
}

/** Une carte par élève d'un stage posé sur une heure rendue (`stageCards`). */
export function stageCards(stage: ApiReleaseStage): UITutorReleaseCard[] {
    const reattributed = stage.outcome === STAGE_OUTCOME.REATTRIBUTED
    const tag = reattributed ? "stage perdu" : stage.keeps_rest ? "reste avec toi" : "remplaçant"
    const tone: UITutorReleaseTone = reattributed ? "lost" : stage.keeps_rest ? "stays" : "atRisk"
    const names = stage.student_names?.length ? stage.student_names : ["Stage"]
    return names.map((name, index) => ({
        id: `st-${stage.live_session_id}-${index}`,
        name,
        tag,
        tone,
        lines: [stageLine(stage)],
    }))
}

/** L'élève d'un essai sur une heure rendue : un autre tuteur le reprend, la famille n'est pas prévenue. */
export const trialCard = (trial: ApiReleaseTrial): UITutorReleaseCard => ({
    id: `t-${trial.live_session_id}`,
    name: trial.student_name ?? "Essai",
    tag: "essai perdu",
    tone: "lost",
    lines: [`Son essai du ${dayDateTimeLabel(trial.date ?? "")} passe à un autre tuteur.`],
})

/** Un cours parti avec son bloc pendant que la famille choisit, sur une heure qu'il rend aussi. */
export const keptSeatLine = (kept: ApiReleaseKeptSeat): string =>
    `Son cours ${kept.subjects ? `de ${kept.subjects} ` : ""}du ${dayTimeLabel(kept.day_time ?? "")} est déjà parti avec son bloc : il ne reviendra pas avec toi, même si sa famille te garde pour ses autres cours.`

/** Un live d'un créneau rendu que la libération laisse en place (une permanence…). */
export function untouchedLiveLine(live: ApiReleaseUntouchedLive): string {
    const when = dayTimeLabel(live.day_time ?? "")
    switch (live.room_type) {
        case "support":
            return `Ta permanence du ${when} n'est pas concernée : elle reste prévue.`
        case "holiday_workbook":
            return `Ton cours d'été du ${when} n'est pas concerné : il reste prévu.`
        default:
            return `Ton cours du ${when} n'est pas concerné : il reste prévu.`
    }
}

/**
 * Combien d'élèves l'aperçu peut lui coûter : ceux qui changent de tuteur sauf si leur famille le
 * garde (une fois chacun), et ceux d'un stage que ses cartes disent perdu. L'élève d'un essai n'est
 * pas encore le sien : un essai perdu ne lève pas la boîte.
 */
export function studentsAtRiskCount(preview: ApiReleasePreview | null | undefined): number {
    const students = new Set(
        (preview?.students ?? [])
            .filter((student) => student.outcome === STUDENT_OUTCOME.REATTRIBUTION)
            .map((student) => String(student.student_id))
    )
    const stageStudents = (preview?.stages ?? []).flatMap(stageCards).filter((card) => card.tone === "lost").length
    return students.size + stageStudents
}

const sameStudent = (a: { student_id?: number | string | null }, b: { student_id?: number | string | null }) =>
    String(a.student_id) === String(b.student_id)

/**
 * L'aperçu entier → ce que « Tes élèves » montre, dans l'ordre de `ReleaseSlotsStudentsStep` : les
 * élèves (le plus grave en tête ; ses rattrapages sur sa première carte), les élèves de passage dont le
 * rattrapage est annulé, les sièges déjà partis, puis les stages et les essais, et les lives non
 * concernés. Un créneau suggéré garde sa clé (`id`, ce que l'écran ouvre) et son nom (« mardi à 17h »).
 */
export function toUITutorReleasePreview(preview: ApiReleasePreview | null | undefined): UITutorReleasePreview | null {
    if (preview == null) return null
    const students = preview.students ?? []
    const rattrapages = preview.rattrapages ?? []
    const rows = studentRowsBySeverity(students)

    const studentCards = rows.map(({ student, row }, index): UITutorReleaseCard => {
        // Un élève dont les deux blocs se séparent a deux cartes : ses rattrapages vont sur la première.
        const first = rows.findIndex((other) => sameStudent(other.student, student)) === index
        return {
            id: `s-${student.student_id}-${index}`,
            name: student.student_name ?? "",
            tag: row.tag,
            tone: row.lost ? "lost" : row.atRisk ? "atRisk" : "stays",
            alert: row.alert ?? undefined,
            lines: [
                ...row.lines,
                ...(first
                    ? rattrapages
                          .filter((r) => sameStudent(r, student))
                          .map((r) => `Rattrapage du ${rattrapageLine(r)}`)
                    : []),
            ],
            suggestion: row.suggestion ?? undefined,
            suggestedSlots: row.suggestedSlots.length
                ? row.suggestedSlots.map((slot) => ({ id: slot, label: dayTimeLabel(slot) }))
                : undefined,
        }
    })
    const visitorCards = rattrapages
        .filter((r) => !students.some((s) => sameStudent(s, r)))
        .map((r): UITutorReleaseCard => ({
            id: `r-${r.student_id}-${r.date}`,
            name: r.student_name ?? "",
            tag: "rattrapage annulé",
            tone: "atRisk",
            lines: [rattrapageLine(r)],
        }))
    const keptSeatCards = (preview.kept_seats ?? []).map((kept): UITutorReleaseCard => ({
        id: `k-${kept.student_id}-${kept.day_time}`,
        name: kept.student_name ?? "",
        tag: "nouveau tuteur",
        tone: "lost",
        lines: [keptSeatLine(kept)],
    }))
    const oneOffCards = [...(preview.stages ?? []).flatMap(stageCards), ...(preview.trials ?? []).map(trialCard)]

    return {
        refusal: preview.refusal || undefined,
        cards: [...studentCards, ...visitorCards, ...keptSeatCards, ...oneOffCards],
        untouchedLines: (preview.untouched_lives ?? []).map(untouchedLiveLine),
    }
}
